import {
  MemoryApiError,
  type MemoryBlock,
  type MemoryProposal,
  type MemoryRevisionCondition,
  type MemoryScope,
} from '@/lib/api/memory.js';

/**
 * Words for the vocabulary workspace memory speaks, plus the derivations the section renders from a
 * block or a proposal. Every lookup here keys on an OPEN union, so each one falls back rather than
 * indexing blindly — a newer service can send a type this build has never seen (CLAUDE.md).
 */

const BLOCK_TYPE_LABELS: Record<string, string> = {
  current_context: 'Current context',
  decision: 'Decision',
  fact: 'Fact',
  convention: 'Convention',
  open_thread: 'Open thread',
  summary: 'Summary',
  preference: 'Preference',
  role: 'Role',
};

const OPERATION_LABELS: Record<string, string> = {
  add: 'Add',
  update: 'Update',
  delete: 'Delete',
  archive: 'Archive',
  change_visibility: 'Change visibility',
  compact: 'Compact',
};

const SOURCE_KIND_LABELS: Record<string, string> = {
  explicit: 'Asked to remember',
  inferred: 'Found in a conversation',
  summary: 'Compacted summary',
  manual_edit: 'Edited by hand',
  recheck: 'Revision condition',
};

const REVISES_IF_KIND_LABELS: Record<string, string> = {
  queryable: 'A system checks this',
  assertable: 'A person checks this',
};

const RECHECK_INTERVAL_LABELS: Record<string, string> = {
  '1d': 'Every day',
  '7d': 'Every 7 days',
  '30d': 'Every 30 days',
  on_demand: 'Only when somebody asks',
};

const REVISION_SOURCE_LABELS: Record<string, string> = {
  candidate: 'From a suggestion',
  manual_edit: 'Edited by hand',
  rollback: 'Undo',
  compaction: 'Compaction',
};

/** Title-cases an unknown vocabulary value so a newer server's word still reads as words. */
export function humanize(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'Memory';
  const spaced = trimmed.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Human label for a block type. */
export function blockTypeLabel(type: string): string {
  return BLOCK_TYPE_LABELS[type] ?? humanize(type);
}

/** Human label for what a proposal wants done. */
export function operationLabel(operation: string): string {
  return OPERATION_LABELS[operation] ?? humanize(operation);
}

/** Human label for where a block's content came from. */
export function sourceKindLabel(sourceKind: string): string {
  return SOURCE_KIND_LABELS[sourceKind] ?? humanize(sourceKind);
}

/** Human label for what wrote one revision. */
export function revisionSourceLabel(sourceKind: string): string {
  return REVISION_SOURCE_LABELS[sourceKind] ?? humanize(sourceKind);
}

/** Human label for how a revision condition is tested. */
export function revisesIfKindLabel(kind: string): string {
  return REVISES_IF_KIND_LABELS[kind] ?? humanize(kind);
}

/** Human label for how often a condition is looked at again. */
export function recheckIntervalLabel(interval: string): string {
  return RECHECK_INTERVAL_LABELS[interval] ?? humanize(interval);
}

/**
 * A block's revision condition in one line — how it is tested and how often (canon §11). Null when
 * the block carries none, which is the caller's cue to say nothing rather than to say "no condition"
 * next to every fact in the workspace.
 *
 * The check itself is deliberately not in here. It is a sentence of its own and the panel shows it on
 * its own row; folding it into this line would produce something too long to scan.
 */
export function revisionConditionSummary(
  condition: Pick<MemoryRevisionCondition, 'kind' | 'recheckInterval'> | null,
): string | null {
  if (condition === null) return null;
  const kind = revisesIfKindLabel(condition.kind);
  if (condition.recheckInterval === null) return kind;
  return `${kind} · ${recheckIntervalLabel(condition.recheckInterval)}`;
}

/**
 * What is left to say about a condition once {@link revisionConditionSummary} has said how it is tested
 * and how often: the question that will be asked, and that a connection answers it.
 *
 * Driven by what the condition CARRIES, not by the word on it — a `queryable` one names a check and a
 * connection, an `assertable` one names neither, and a kind this build has never seen is described by
 * whichever of the two it turns up with rather than by a guess.
 *
 * `connectionLabel` is the connection's name where the caller can be told it, and null where they
 * cannot — the label is unresolved, still loading, or belongs to a connection this person does not
 * reach. Then the line says that much and stops. A raw uuid on screen tells nobody anything.
 */
export function revisionConditionDetailLines(
  condition: Pick<MemoryRevisionCondition, 'check' | 'connectionId'>,
  connectionLabel: string | null,
): string[] {
  const lines: string[] = [];
  const check = condition.check?.trim() ?? '';
  if (check !== '') lines.push(`Check: ${check}`);
  const connectionId = condition.connectionId?.trim() ?? '';
  if (connectionId !== '') {
    lines.push(
      connectionLabel === null
        ? 'It runs through a connection that is not named here.'
        : `It runs through ${connectionLabel}.`,
    );
  }
  return lines;
}

/**
 * The one sentence that says what accepting does to the condition — and what the reviewer's own Edit
 * does not reach. The approver may reword the memory and may not touch the condition, and a person who
 * learns that from a saved edit that changed nothing has learned it the hard way.
 */
export const MEMORY_CONDITION_ON_ACCEPT =
  'Accepting puts this condition on the memory. You can reword the text here, but not the condition.';

/**
 * The mark on a decision that carries no revision condition (canon §3.2 and §16.6). Words, never a
 * colour: it is the whole point of the mark that a person reads what is missing.
 *
 * `is_note` is answered by the server for exactly this case and is read rather than derived — a `fact`
 * never carries the flag, so a fact can never be marked here however it is stored.
 */
export const MEMORY_NOTE_MARK = 'Note';

/** The one sentence that says what the mark means, shown where there is room for it. */
export const MEMORY_NOTE_EXPLANATION =
  'A decision with no revision condition is kept as a note.';

/** What the two documents are called on screen, matching the prototype's scope switcher. */
export function scopeLabel(scope: MemoryScope): string {
  return scope === 'shared' ? 'Shared' : 'My memory';
}

/**
 * The two documents named as they read mid-sentence — "saved to shared memory", "added to your
 * personal memory". Its own function rather than a lower-cased {@link scopeLabel}, which would produce
 * "my memory memory" and would flatten the capitals of whatever section name follows it.
 */
export function memoryName(scope: MemoryScope): string {
  return scope === 'shared' ? 'shared memory' : 'your personal memory';
}

/** The heading the document tab carries, which follows the scope switcher. */
export function documentHeading(scope: MemoryScope): string {
  return scope === 'shared' ? 'Shared memory.md' : 'My memory.md';
}

/**
 * A one-line title for a block, taken from its own text: Markdown decoration stripped, first line,
 * clipped. Blocks carry no title field — the document is Markdown, and inventing a separate title
 * would be a second place for the same words to disagree.
 */
export function blockTitle(contentMarkdown: string): string {
  const firstLine =
    contentMarkdown
      .split('\n')
      .map((line) => line.replace(/^[\s>*\-+]*(#{1,6}\s*)?/, '').trim())
      .find((line) => line.length > 0) ?? '';
  if (firstLine.length <= 72) return firstLine;
  return `${firstLine.slice(0, 71).trimEnd()}…`;
}

/**
 * What the card shows under the title: the rest of the memory's own text.
 *
 * The first line is already the title (see {@link blockTitle}), and repeating it under itself is how a
 * two-line memory ends up saying the same thing twice. A memory of one line has no body at all.
 */
export function blockBody(contentMarkdown: string): string {
  const lines = contentMarkdown.split('\n');
  const first = lines.findIndex(
    (line) => line.replace(/^[\s>*\-+]*(#{1,6}\s*)?/, '').trim() !== '',
  );
  if (first < 0) return '';
  return lines
    .slice(first + 1)
    .join('\n')
    .trim();
}

/**
 * Where a block came from, in words. Never the private conversation itself: a shared block can be born
 * in somebody's personal chat, and naming it would leak through the very door the scope model closes
 * (canon §7.1). The kind of source is enough to judge a memory by.
 */
export function blockSourceLabel(block: MemoryBlock): string | null {
  const kind = sourceKindLabel(block.sourceKind);
  if (block.scope === 'personal') return kind;
  return block.sourceConversationId === null ? kind : `${kind} · shared chat`;
}

/**
 * The same rule for a proposal's provenance line. A proposal against the shared document says the
 * conversation was a shared one; a personal one says nothing about which chat it was.
 */
export function proposalSourceLabel(proposal: MemoryProposal): string {
  if (proposal.origin === 'recheck') return 'Revision condition';
  if (proposal.origin === 'compaction') return 'Compaction';
  if (proposal.origin === 'manual_edit') return 'Added by hand';
  if (proposal.sourceConversationId === null) return 'From a conversation';
  return proposal.scope === 'shared'
    ? 'From a shared chat'
    : 'From a personal chat';
}

/**
 * Whether this proposal asks to widen who can see a personal fact. It is its own kind of card: the
 * answer is not "is this true" but "may other people read it", and only the owner may give it
 * (canon §4), so it never rides a bulk action.
 */
export function isVisibilityChange(proposal: MemoryProposal): boolean {
  return proposal.operation === 'change_visibility';
}

/**
 * Whether a proposal may be swept up by **Accept safe changes**.
 *
 * Only independent, safe suggestions qualify: a `conflicted` one has to be recomputed against a
 * document that moved, a visibility change hands somebody else's eyes a private fact, and a delete or
 * an archive takes something away. Low confidence is left out too — a bulk button exists to clear the
 * obvious, and anything the platform is unsure of deserves the person's attention.
 */
export function isSafeToBulkAccept(proposal: MemoryProposal): boolean {
  if (proposal.status !== 'pending') return false;
  if (isVisibilityChange(proposal)) return false;
  if (proposal.operation === 'delete' || proposal.operation === 'archive') {
    return false;
  }
  return proposal.confidence !== 'low';
}

/** Whether this caller may answer a proposal, mirroring the server's `required_approver` check. */
export function canAnswerProposal(
  proposal: MemoryProposal,
  role: string | null,
): boolean {
  if (proposal.requiredApprover === 'personal_owner') {
    return proposal.scope === 'personal';
  }
  return role === 'owner' || role === 'editor';
}

/**
 * Whether this caller may edit one memory in place — the client's mirror of the service's
 * `canEditBlock`.
 *
 * The shared document's write right plus one lane: whoever **brought** a block may also change it,
 * which is what lets a `viewer` who confirmed a fact reword it instead of filing a proposal against
 * their own memory (canon §4). A personal document answers to its owner alone, and there the caller is
 * the owner by construction.
 *
 * A block nobody is recorded as bringing is governed by the role alone — `null === null` must never
 * read as a match, which is why the null is tested rather than compared.
 */
export function canEditMemoryBlock(
  block: Pick<MemoryBlock, 'scope' | 'createdByUserId'>,
  role: string | null,
  currentUserId: string | null,
): boolean {
  if (block.scope === 'personal') return true;
  if (role === 'owner' || role === 'editor') return true;
  return (
    block.createdByUserId !== null && block.createdByUserId === currentUserId
  );
}

/**
 * Whether this caller may write to one document by hand — save it, or undo one of its revisions.
 * The personal document is the caller's own by construction; the shared one needs editor or owner
 * (canon §4). Fail-closed on an unresolved or unknown role, like every other mirror of a server rule.
 */
export function canWriteMemory(
  scope: MemoryScope,
  role: string | null,
): boolean {
  if (scope === 'personal') return true;
  return role === 'owner' || role === 'editor';
}

/**
 * The character ceiling the counter is drawn against — canon §18's `document_limit_chars` default.
 *
 * A mirror, not the authority. The live number lives in one `platform_settings` row the SPA has no
 * route to, and the server is the one that enforces it: a save over the real limit is answered 422
 * with words that say so. Showing the default here beats showing no ceiling at all.
 */
export const MEMORY_DOCUMENT_LIMIT_CHARS = 16_000;

/**
 * The ceiling one memory's own text is counted against — `MAX_BLOCK_CONTENT_LENGTH` in the service's
 * patch DTO. A mirror, like the document limit above: the service is what enforces it, and a card that
 * showed no ceiling at all would leave somebody to discover it through a 422.
 */
export const MEMORY_BLOCK_LIMIT_CHARS = 20_000;

/** `8,421 / 16,000`, the counter the prototype puts beside Save changes. */
export function formatCharacterCount(used: number, limit: number): string {
  return `${used.toLocaleString('en-US')} / ${limit.toLocaleString('en-US')}`;
}

/**
 * Why answering a suggestion did not work, in words somebody can act on.
 *
 * The 409 is the one worth translating. The server says "this memory document is on version 4 — re-read
 * it and retry", which is true and useless: there is nothing on this screen to re-read, and the
 * proposal has already moved to `conflicted` by the time the answer arrives (canon §19). What a person
 * needs to know is that memory moved on underneath the suggestion, and that the suggestion is closed
 * rather than lost.
 *
 * Everything else is passed through as the server phrased it. A message invented here would be a second
 * account of what happened, free to disagree with the first.
 */
export function answerFailureMessage(error: unknown): string | null {
  if (!(error instanceof MemoryApiError)) {
    return error instanceof Error ? error.message : null;
  }
  if (error.status === 409) {
    return 'This memory changed after the suggestion was made, so it could not be applied. The suggestion is closed — ask again in the conversation it came from, or add the memory by hand.';
  }
  return error.message;
}
